import { Card, CardContent } from "@/components/ui/card";

// Client referral status type based on schema
type ClientReferralStatus = 'pending' | 'contacted' | 'meeting_scheduled' | 'converted' | 'rejected';

// Custom SVG icons for client referral statuses
const PendingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
  </svg>
);

const ContactedIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
  </svg>
);

const MeetingScheduledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
  </svg>
);

const ConvertedIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
  </svg>
);

const RejectedIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
  </svg>
);

interface ClientReferralStatusCardProps {
  status: ClientReferralStatus;
  title: string;
  value: string | number;
  variant?: "default" | "dark";
  testId?: string;
}

const getStatusIcon = (status: ClientReferralStatus) => {
  switch (status) {
    case 'pending':
      return <PendingIcon />;
    case 'contacted':
      return <ContactedIcon />;
    case 'meeting_scheduled':
      return <MeetingScheduledIcon />;
    case 'converted':
      return <ConvertedIcon />;
    case 'rejected':
      return <RejectedIcon />;
    default:
      return <PendingIcon />;
  }
};

const getStatusIconColor = (status: ClientReferralStatus, isDark: boolean) => {
  if (isDark) {
    return "text-white";
  }
  
  // All non-dark icons should be orange to match the design system
  return "text-orange-600";
};

export function ClientReferralStatusCard({ 
  status,
  title, 
  value, 
  variant = "default",
  testId 
}: ClientReferralStatusCardProps) {
  const isDark = variant === "dark";
  
  return (
    <Card 
      className={`border-0 shadow-sm ${isDark ? "text-white" : ""}`}
      style={isDark ? { backgroundColor: '#2D2D2D' } : {}}
      data-testid={testId}
    >
      <CardContent 
        className="p-0"
        style={{
          display: 'flex',
          padding: '16px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px'
        }}
      >
        <div className={`h-6 w-6 sm:h-7 sm:w-7 ${getStatusIconColor(status, isDark)}`}>
          {getStatusIcon(status)}
        </div>
        <p className={`text-2xl sm:text-3xl font-bold space-grotesk ${
          isDark ? "text-white" : "text-gray-900"
        }`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className={`text-xs font-medium uppercase space-grotesk leading-tight tracking-[0.07px] ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}>
          {title}
        </p>
      </CardContent>
    </Card>
  );
}

// Export the status type for use in other components
export type { ClientReferralStatus };
